import { Button, Classes, Colors, InputGroup, Intent, Position } from '@blueprintjs/core';
import Avatar from '@components/Avatar';
import Circle from '@components/Circle';
import Flex from '@components/Flex';
import PlatformFilterButton from '@components/PlatformFilterButton';
import PlatformListFilterWithDynamicSearch from '@components/PlatformListFilterWithDynamicSearch';
import { platformFiltersInformation } from '@pages/Tasks/consts';
import { PlatformTaskFilterType } from '@pages/Tasks/enums';
import { fetchCoreUserData } from '@pages/Tasks/store/effects';
import { setPlatformTaskFilterData } from '@pages/Tasks/store/events';
import { $isQueryHydrationFinished } from '@pages/Tasks/store/states';
import { PlatformTaskFilterData } from '@pages/Tasks/types';
import { CoreUserResource } from 'dy-frontend-http-repository/lib/modules/Filter/resources';
import { useStore } from 'effector-react';
import React, { useEffect, useRef, useState } from 'react';
import DevText from '@components/Text';

export interface TaskParticipantsPlatformFilterProps {
    defaultIsOpen?: boolean;
    platformTaskFilterData: PlatformTaskFilterData;
    onRemove: () => void;
}

export type Props = TaskParticipantsPlatformFilterProps;

const TaskParticipantsPlatformFilter: React.FC<Props> = ({
    defaultIsOpen = false,
    platformTaskFilterData,
    onRemove,
}) => {
    const isQueryHydrationFinished = useStore($isQueryHydrationFinished);

    const [isOpen, setIsOpen] = useState(defaultIsOpen);
    const [query, setQuery] = useState('');
    const [isParticipantsSearching, setIsParticipantsSearching] = useState(false);
    const [selectedParticipants, setSelectedParticipants] = useState<PlatformTaskFilterData['participants']>([]);
    const [foundParticipants, setFoundParticipants] = useState<PlatformTaskFilterData['participants']>([]);

    const searchInputRef = useRef<null | HTMLInputElement>(null);

    useEffect(() => {
        if (query.trim().length === 0) {
            setFoundParticipants([]);
        }
    }, [query]);

    useEffect(() => {
        if (isQueryHydrationFinished) {
            setSelectedParticipants(platformTaskFilterData.participants);
        }

        // eslint-disable-next-line
    }, [isQueryHydrationFinished]);

    useEffect(() => {
        if (query.trim().length !== 0) {
            fetchCoreUserData({ query, exclude_id: selectedParticipants.map((participant) => participant.id) }).then(
                (participants) => setFoundParticipants(participants.items)
            );
        }

        handleFocusInput();

        // eslint-disable-next-line
    }, [selectedParticipants]);

    useEffect(() => {
        setSelectedParticipants(platformTaskFilterData.participants);
        setFoundParticipants([]);
    }, [platformTaskFilterData.participants]);

    const getFilterTagTitle = () => {
        return selectedParticipants.length > 0 ? 'Participant:' : 'Participant';
    };

    const getFilterTagLabel = () => {
        if (selectedParticipants.length === 0) return '';

        return selectedParticipants
            .map((participant, index) => {
                if (index === selectedParticipants.length - 1) {
                    // Last element
                    return `${participant.first_name} ${participant.last_name}`;
                }

                return `${participant.first_name} ${participant.last_name}, `;
            })
            .join('');
    };

    const handleRemoveFilter = () => {
        if (platformTaskFilterData.participants.length !== 0) {
            // Reset participants to empty array
            setPlatformTaskFilterData({ ...platformTaskFilterData, participants: [] });
        }

        if (!platformFiltersInformation[PlatformTaskFilterType.TASK_PARTICIPANTS].isPinned) {
            // Platform participant filter is NOT pinned, so it can be removed
            onRemove();
        }
    };

    const handleSearch = (searchValue: string = query) => {
        setIsParticipantsSearching(true);

        if (searchValue.trim().length !== 0) {
            // Search value is NOT empty string

            // Fetch users matched by searchValue
            fetchCoreUserData({
                query: searchValue,
                exclude_id: selectedParticipants.map((participant) => participant.id),
            })
                .then((participants) => setFoundParticipants(participants.items))
                .finally(() => setTimeout(() => setIsParticipantsSearching(false), 1000));
        }
    };

    const handleAddParticipant = (newParticipant: CoreUserResource) => {
        if (!selectedParticipants.find((participant) => participant.id === newParticipant.id)) {
            // Participant with such ID was not added yet
            setSelectedParticipants([newParticipant, ...selectedParticipants]);
        }
    };

    const handleRemoveParticipant = (id: ID) => {
        setSelectedParticipants(selectedParticipants.filter((participant) => participant.id !== id));
    };

    const handleFocusInput = () => {
        if (searchInputRef.current !== null) {
            // Focus input
            searchInputRef.current.focus();
        }
    };

    const handleApplyFilter = () => {
        if (JSON.stringify(selectedParticipants) !== JSON.stringify(platformTaskFilterData.participants)) {
            // If nothing changed since opening the filter then nothing apply to
            setPlatformTaskFilterData({ ...platformTaskFilterData, participants: selectedParticipants });
        }
    };

    const handleCloseFilter = () => {
        setIsOpen(false);
        setQuery('');

        if (platformFiltersInformation[PlatformTaskFilterType.TASK_PARTICIPANTS].isPinned) {
            return;
        }

        if (selectedParticipants.length === 0) {
            onRemove();
        }
    };

    const renderSearchInput = () => {
        return (
            <InputGroup
                leftIcon="search"
                placeholder="Search for participants..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                inputRef={searchInputRef}
            />
        );
    };

    const renderSkeletonRow = (id: ID) => {
        return (
            <li key={id}>
                <Flex justify="space-between">
                    <Flex className="mr-2">
                        <Avatar alt="name" className={`mr-2 ${Classes.SKELETON}`} width="35px" height="35px" />

                        <Flex align="center">
                            <Circle className={`mr-1 ${Classes.TEXT_MUTED}`} size="12px" color={Colors.GRAY2} />

                            <DevText className={Classes.SKELETON}>fake team</DevText>
                        </Flex>
                    </Flex>

                    <Button minimal icon="cross" className={Classes.SKELETON} intent={Intent.DANGER} />
                </Flex>
            </li>
        );
    };

    const renderSelectedParticipantRow = ({ id, first_name, last_name, team_participation }: CoreUserResource) => {
        const teamParticipation = team_participation[0];

        return (
            <li key={id}>
                <Flex justify="space-between">
                    <Flex className="mr-2">
                        <Avatar className="mr-2" width="35px" height="35px" alt={`${first_name} ${last_name}`} />

                        <Flex direction="column" align="flex-start">
                            <DevText>
                                {first_name} {last_name}
                            </DevText>

                            <Flex align="center">
                                <Circle
                                    className="mr-1"
                                    size="12px"
                                    color={teamParticipation ? teamParticipation.team.color : 'none'}
                                    borderColor={teamParticipation ? 'none' : Colors.BLACK}
                                />

                                <DevText muted>{teamParticipation ? teamParticipation.team.title : 'No team'}</DevText>
                            </Flex>
                        </Flex>
                    </Flex>

                    <Button minimal icon="cross" intent={Intent.DANGER} onClick={() => handleRemoveParticipant(id)} />
                </Flex>
            </li>
        );
    };

    const renderFoundParticipantRow = ({
        id,
        first_name,
        last_name,
        team_participation,
        image_file_preview_url,
    }: CoreUserResource) => {
        const teamParticipation = team_participation[0];

        return (
            <li key={id}>
                <Flex justify="space-between">
                    <Flex className="mr-2">
                        <Avatar className="mr-2" alt={`${first_name} ${last_name}`} width="35px" height="35px" />

                        <Flex direction="column" align="flex-start">
                            <DevText>
                                {first_name} {last_name}
                            </DevText>

                            <Flex align="center">
                                <Circle
                                    className="mr-1"
                                    size="12px"
                                    color={teamParticipation ? teamParticipation.team.color : 'none'}
                                    borderColor={teamParticipation ? 'none' : Colors.BLACK}
                                />
                                <DevText muted>{teamParticipation ? teamParticipation.team.title : 'No team'}</DevText>
                            </Flex>
                        </Flex>
                    </Flex>

                    <Button
                        minimal
                        icon="plus"
                        intent={Intent.SUCCESS}
                        onClick={() =>
                            handleAddParticipant({
                                id,
                                first_name,
                                last_name,
                                team_participation,
                                image_file_preview_url,
                            })
                        }
                    />
                </Flex>
            </li>
        );
    };

    const renderEmptyList = () => {
        return (
            <div>
                <Flex className="mb-1" align="center">
                    <Circle className="mr-1" color={Colors.GRAY2} size="16px" />
                    <DevText>No results</DevText>
                </Flex>
                <DevText muted>Please try different search query</DevText>
            </div>
        );
    };

    return (
        <PlatformListFilterWithDynamicSearch<CoreUserResource>
            loading={isParticipantsSearching && query.trim().length > 0}
            isOpen={isOpen}
            removable={selectedParticipants.length > 0}
            title="Participant"
            position={Position.BOTTOM_LEFT}
            search={query}
            maxListHeight={318}
            selectedList={selectedParticipants}
            foundList={foundParticipants}
            skeletonList={Array.from({ length: 3 }, (_, index) => `${index + 1}`)}
            selectedItemRenderer={renderSelectedParticipantRow}
            foundItemRenderer={renderFoundParticipantRow}
            emptyListRenderer={renderEmptyList}
            searchInputRenderer={renderSearchInput}
            skeletonItemRenderer={renderSkeletonRow}
            onSearch={handleSearch}
            onApply={handleApplyFilter}
            onClose={handleCloseFilter}
            onRemove={handleRemoveFilter}
            onOpened={handleFocusInput}
        >
            <PlatformFilterButton
                icon={platformFiltersInformation[PlatformTaskFilterType.TASK_PARTICIPANTS].icon}
                onClick={() => setIsOpen(!isOpen)}
                label={getFilterTagLabel()}
                title={getFilterTagTitle()}
            />
        </PlatformListFilterWithDynamicSearch>
    );
};

export default TaskParticipantsPlatformFilter;
